<template>
  <div id="app">
    <router-view />
    
    <!-- 自定义通知弹窗 -->
    <div v-if="notificationVisible" class="notification-overlay">
      <div class="notification-box" :class="`notification-${notification.type || 'info'}`">
        <div class="notification-header">
          <div class="notification-icon">
            <i :class="getNotificationIcon(notification.type)"></i>
          </div>
          <h4 class="notification-title">{{ notification.title || getDefaultTitle(notification.type) }}</h4>
          <button v-if="notification.showClose !== false" class="notification-close" @click="closeNotification">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="notification-body">
          <p>{{ notification.message }}</p>
        </div>
        <div v-if="notification.showButton !== false" class="notification-footer">
          <button class="notification-btn" @click="closeNotification">
            {{ notification.closeButtonText || '确定' }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- 自定义确认弹窗 -->
    <div v-if="confirmVisible" class="confirm-overlay" @click="handleOverlayClick">
      <div class="confirm-dialog" @click.stop>
        <div class="confirm-header">
          <h3 class="confirm-title">{{ confirm.title }}</h3>
          <button v-if="confirm.showClose !== false" class="confirm-close" @click="cancelConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="confirm-body">
          <p>{{ confirm.message }}</p>
        </div>
        <div class="confirm-footer">
          <button class="confirm-btn confirm-btn-cancel" @click="cancelConfirm">
            {{ confirm.cancelButtonText || '取消' }}
          </button>
          <button class="confirm-btn confirm-btn-primary" @click="submitConfirm">
            {{ confirm.confirmButtonText || '确认' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, provide } from 'vue';

// 通知弹窗状态
const notificationVisible = ref(false);
const notification = ref({
  title: '',
  message: '',
  type: 'info', // success, error, warning, info
  showClose: true,
  showButton: true,
  closeButtonText: '确定',
  duration: 0 // 0 表示不自动关闭
});

let notificationTimer = null;

// 通知图标映射
const getNotificationIcon = (type) => {
  const iconMap = {
    success: 'fas fa-check-circle',
    error: 'fas fa-times-circle',
    warning: 'fas fa-exclamation-triangle',
    info: 'fas fa-info-circle'
  };
  return iconMap[type] || iconMap.info;
};

// 默认标题映射
const getDefaultTitle = (type) => {
  const titleMap = {
    success: '成功',
    error: '错误',
    warning: '警告',
    info: '提示'
  };
  return titleMap[type] || titleMap.info;
};

// 确认弹窗状态
const confirmVisible = ref(false);
const confirm = ref({
  title: '',
  message: '',
  width: '30%',
  showClose: true,
  cancelButtonText: '取消',
  confirmButtonText: '确认',
  callback: null,
  resolve: null,  // 添加 resolve 函数引用
  onCancel: null,
  onConfirm: null
});

// 打开确认弹窗
const openConfirm = (options) => {
  return new Promise((resolve) => {
    confirm.value = { ...confirm.value, ...options, resolve };
    confirmVisible.value = true;
  });
};

// 取消确认
const cancelConfirm = () => {
  confirmVisible.value = false;
  if (typeof confirm.value.onCancel === 'function') {
    confirm.value.onCancel();
  }
  // 调用 resolve 并传入 false 表示取消
  if (typeof confirm.value.resolve === 'function') {
    confirm.value.resolve(false);
  }
};

// 提交确认
const submitConfirm = () => {
  confirmVisible.value = false;
  if (typeof confirm.value.onConfirm === 'function') {
    confirm.value.onConfirm();
  }
  // 调用 resolve 并传入 true 表示确认
  if (typeof confirm.value.resolve === 'function') {
    confirm.value.resolve(true);
  }
};

// 打开通知弹窗
const openNotification = (options) => {
  // 清除之前的定时器
  if (notificationTimer) {
    clearTimeout(notificationTimer);
    notificationTimer = null;
  }
  
  // 合并选项
  notification.value = { 
    title: '',
    message: '',
    type: 'info',
    showClose: true,
    showButton: true,
    closeButtonText: '确定',
    duration: 0,
    ...options 
  };
  
  notificationVisible.value = true;
  
  // 如果设置了自动关闭时间
  if (notification.value.duration > 0) {
    notificationTimer = setTimeout(() => {
      closeNotification();
    }, notification.value.duration);
  }
};

// 关闭通知弹窗
const closeNotification = () => {
  notificationVisible.value = false;
  
  // 清除定时器
  if (notificationTimer) {
    clearTimeout(notificationTimer);
    notificationTimer = null;
  }
  
  if (typeof notification.value.onClose === 'function') {
    notification.value.onClose();
  }
};

// 处理遮罩层点击
const handleOverlayClick = () => {
  if (confirm.value.closeOnClickModal !== false) {
    cancelConfirm();
  }
};

// 提供给所有组件使用
provide('notification', {
  open: openNotification,
  close: closeNotification
});

provide('confirm', {
  open: openConfirm,
  cancel: cancelConfirm,
  submit: submitConfirm
});
</script>

<style>
/* 全局滚动条美化样式 */
/* 适用于Webkit内核浏览器（Chrome、Safari、Edge等） */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: linear-gradient(90deg, #181a20 60%, #23263a 100%);
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #23263a 60%, #3a3f5a 90%, #6a5af9 100%);
  border-radius: 10px;
  transition: all 0.3s ease;
  border: 1px solid #23263a;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #23263a 40%, #6a5af9 100%);
  box-shadow: 0 0 8px #6a5af980;
}

::-webkit-scrollbar-corner {
  background: #181a20;
}

/* Firefox滚动条样式 */
html {
  scrollbar-width: thin;
  scrollbar-color: #6a5af9 #23263a;
}

/* 提升滚动体验 */
* {
  scroll-behavior: smooth;
}

/* 自定义通知样式 */
.notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
  animation: fadeIn 0.3s ease-out;
}

.notification-box {
  background: linear-gradient(145deg, rgba(30, 34, 44, 0.98), rgba(40, 45, 58, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(20px);
  width: 90%;
  max-width: 450px;
  min-width: 320px;
  animation: slideInScale 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
}

.notification-header {
  display: flex;
  align-items: center;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.notification-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 18px;
}

.notification-success .notification-icon {
  background: linear-gradient(135deg, #10b981, #065f46);
  color: #fff;
}

.notification-error .notification-icon {
  background: linear-gradient(135deg, #ef4444, #7f1d1d);
  color: #fff;
}

.notification-warning .notification-icon {
  background: linear-gradient(135deg, #f59e0b, #78350f);
  color: #fff;
}

.notification-info .notification-icon {
  background: linear-gradient(135deg, #3b82f6, #1e3a8a);
  color: #fff;
}

.notification-title {
  flex: 1;
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.notification-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.notification-close:hover {
  color: #fff;
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-body {
  padding: 0 24px 20px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  line-height: 1.6;
}

.notification-body p {
  margin: 0;
}

.notification-footer {
  padding: 0 24px 24px;
  display: flex;
  justify-content: flex-end;
}

.notification-btn {
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
  min-width: 100px;
}

.notification-btn:hover {
  background: linear-gradient(135deg, #2563eb, #1e40af);
  box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
  transform: translateY(-1px);
}

.notification-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

/* 不同类型的边框颜色 */
.notification-success {
  border-left: 4px solid #10b981;
}

.notification-error {
  border-left: 4px solid #ef4444;
}

.notification-warning {
  border-left: 4px solid #f59e0b;
}

.notification-info {
  border-left: 4px solid #3b82f6;
}

/* 动画效果 */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideInScale {
  from {
    opacity: 0;
    transform: scale(0.8) translateY(-30px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* 全局弹窗暗色主题样式 - 使用更强的选择器优先级 */
.el-dialog.custom-dialog {
  background-color: rgba(30, 34, 44, 0.98) !important;
  border: 1px solid rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 12px !important;
}

.el-dialog__header.custom-dialog .el-dialog__header,
.custom-dialog .el-dialog__header {
  background-color: transparent !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1) !important;
  padding: 20px 24px 16px !important;
}

.el-dialog__title.custom-dialog .el-dialog__title,
.custom-dialog .el-dialog__title {
  color: #fff !important;
  font-size: 18px !important;
  font-weight: 600 !important;
}

.el-dialog__headerbtn.custom-dialog .el-dialog__headerbtn,
.custom-dialog .el-dialog__headerbtn {
  color: #fff !important;
}

.el-dialog__headerbtn:hover.custom-dialog .el-dialog__headerbtn:hover,
.custom-dialog .el-dialog__headerbtn:hover {
  color: #66b1ff !important;
}

.el-dialog__body.custom-dialog .el-dialog__body,
.custom-dialog .el-dialog__body {
  background-color: transparent !important;
  color: #fff !important;
  padding: 24px !important;
  font-size: 14px !important;
  line-height: 1.6 !important;
}

.el-dialog__footer.custom-dialog .el-dialog__footer,
.custom-dialog .el-dialog__footer {
  background-color: transparent !important;
  border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
  padding: 16px 24px 20px !important;
}

.el-button.custom-dialog .el-button,
.custom-dialog .el-button {
  border-radius: 6px !important;
  padding: 8px 20px !important;
  font-weight: 500 !important;
}

.el-button--default.custom-dialog .el-button--default,
.custom-dialog .el-button--default {
  background-color: #f5f7fa !important;
  border: 1px solid #dcdfe6 !important;
  color: #606266 !important;
}

.el-button--default:hover.custom-dialog .el-button--default:hover,
.custom-dialog .el-button--default:hover {
  background-color: #ecf5ff !important;
  border-color: #c6e2ff !important;
  color: #409eff !important;
}

.el-button--primary.custom-dialog .el-button--primary,
.custom-dialog .el-button--primary {
  background-color: #409EFF !important;
  border-color: #409EFF !important;
  color: #fff !important;
}

.el-button--primary:hover.custom-dialog .el-button--primary:hover,
.custom-dialog .el-button--primary:hover {
  background-color: #66b1ff !important;
  border-color: #66b1ff !important;
}

.el-overlay {
  background-color: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(3px) !important;
}

/* 确保弹窗内容文本颜色正确 */
.custom-dialog span,
.custom-dialog .el-dialog__body span {
  color: #fff !important;
}

.custom-dialog .dialog-footer {
  display: flex !important;
  justify-content: flex-end !important;
  gap: 12px !important;
}

/* 更强力的覆盖 */
.el-dialog.is-fullscreen .custom-dialog,
.el-dialog.custom-dialog,
.custom-dialog.el-dialog {
  background-color: rgba(30, 34, 44, 0.98) !important;
}

body .el-dialog.custom-dialog {
  background-color: rgba(30, 34, 44, 0.98) !important;
}

body .el-dialog.custom-dialog .el-dialog__body {
  color: #fff !important;
}

body .el-dialog.custom-dialog .el-dialog__title {
  color: #fff !important;
}

/* 强制覆盖取消按钮样式 */
body .el-dialog.custom-dialog .el-button--default,
body .custom-dialog .el-button--default,
.el-dialog.custom-dialog .el-button--default {
  background-color: #f5f7fa !important;
  border: 1px solid #dcdfe6 !important;
  color: #606266 !important;
}

body .el-dialog.custom-dialog .el-button--default:hover,
body .custom-dialog .el-button--default:hover,
.el-dialog.custom-dialog .el-button--default:hover {
  background-color: #ecf5ff !important;
  border-color: #c6e2ff !important;
  color: #409eff !important;
}

/* 自定义确认弹窗样式 */
.confirm-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  animation: fadeIn 0.3s ease-out;
}

.confirm-dialog {
  background-color: rgba(30, 34, 44, 0.98);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(10px);
  width: 90%;
  max-width: 420px;
  animation: slideIn 0.3s ease-out;
}

.confirm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.confirm-title {
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0;
}

.confirm-close {
  background: none;
  border: none;
  color: #fff;
  font-size: 16px;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.confirm-close:hover {
  color: #66b1ff;
  background-color: rgba(255, 255, 255, 0.1);
}

.confirm-body {
  padding: 24px;
  color: #fff;
  font-size: 14px;
  line-height: 1.6;
}

.confirm-body p {
  margin: 0;
}

.confirm-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}

.confirm-btn {
  border: none;
  border-radius: 6px;
  padding: 8px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
}

.confirm-btn-cancel {
  background-color: #f5f7fa;
  color: #606266;
  border: 1px solid #dcdfe6;
}

.confirm-btn-cancel:hover {
  background-color: #ecf5ff;
  color: #409eff;
  border-color: #c6e2ff;
}

.confirm-btn-primary {
  background-color: #409EFF;
  color: #fff;
  border: 1px solid #409EFF;
}

.confirm-btn-primary:hover {
  background-color: #66b1ff;
  border-color: #66b1ff;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}
</style>